import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

export type RouteParams = {
  [key: string]: string;
};

type RouterContextValue = {
  pathname: string;
  params: RouteParams;
  navigate: (path: string) => void;
};

const RouterContext = createContext<RouterContextValue | null>(null);

export function RouterProvider({
  initialPath = '/',
  children,
}: {
  initialPath?: string;
  children: ReactNode;
}) {
  const [path, setPath] = useState(initialPath);

  const navigate = useCallback((newPath: string) => {
    setPath(newPath);
  }, []);

  const pathname = getPathname(path);
  const params = useMemo(() => parsePath(path), [path]);

  return (
    <RouterContext.Provider value={{ pathname, params, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error('useRouter must be used within a RouterProvider');
  return ctx;
}

function getPathname(path: string): string {
  return path.split('?')[0] || '/';
}

function parsePath(path: string): RouteParams {
  const params: RouteParams = {};

  const search = path.split('?')[1];
  if (!search) return params;

  for (const [key, value] of new URLSearchParams(search)) {
    params[key] = value;
  }

  return params;
}
